import {
  MARKDOWN_HARD_BREAK_LINE_ENDING,
  collapseNewlines,
  isMarkdownVisuallyEmpty,
  SPARX_VARIABLE_REGEX,
  trimMarkdownOuterNewlines,
} from "@/lib/markdown/markdownShared";
import type { ListContent, PhrasingContent, RootContent } from "mdast";
import { remark } from "remark";
import remarkBreaks from "remark-breaks";

const PLACEHOLDER_PREFIX = "\uE000SPARX_VAR_";
const PLACEHOLDER_SUFFIX = "\uE000";

/** For Rich Text Editor JSON bridge; must match placeholders used in {@link parseSparxMarkdownAst}. */
export const SPARX_MARKDOWN_VARIABLE_PLACEHOLDER = {
  prefix: PLACEHOLDER_PREFIX,
  suffix: PLACEHOLDER_SUFFIX,
} as const;

export type SparxMarkdownAst = {
  /** Top-level mdast blocks (paragraph, list, thematicBreak, heading, …). */
  rootChildren: RootContent[];
  /** Variable matches for `{{…}}` replaced by placeholders during parse (same order as in text). */
  variableSegments: string[];
};

const NBSP = "\u00a0";

/**
 * Remark/micromark drops leading spaces on continuation lines inside paragraphs.
 * Temporarily replace 1–3 spaces after a line start with NBSP so advisor-style
 * indentation survives parse (four-space indented code blocks stay ASCII).
 */
const protectRemarkContinuationIndent = (md: string): string =>
  md.replace(
    /(^|\n)( {1,3})(?=\S)/g,
    (_m, lineStart: string, spaces: string) =>
      `${lineStart}${NBSP.repeat(spaces.length)}`,
  );

const restoreMarkdownIndentNbsp = (md: string): string =>
  md.replace(/\u00a0/g, " ").replace(/&nbsp;/gi, " ");

/** Shared remark parse used by HTML export and TipTap JSON import (WYSIWYG). */
export const parseSparxMarkdownAst = (md: string): SparxMarkdownAst => {
  const s = trimMarkdownOuterNewlines(md);
  if (isMarkdownVisuallyEmpty(s)) {
    return { rootChildren: [], variableSegments: [] };
  }
  const [protectedStr, variableSegments] = protectVariables(
    protectRemarkContinuationIndent(s),
  );
  const escapedForParse = escapeHtmlBody(protectedStr);
  const processor = remark().use(remarkBreaks);
  try {
    const tree = processor.parse(escapedForParse);
    return {
      rootChildren: tree.children,
      variableSegments,
    };
  } catch {
    return { rootChildren: [], variableSegments };
  }
};
const DATA_SPARX_VAR = "data-sparx-var";

const escapeHtmlAttr = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeHtmlBody = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const protectVariables = (s: string): [string, string[]] => {
  const segments: string[] = [];
  SPARX_VARIABLE_REGEX.lastIndex = 0;
  const result = s.replace(SPARX_VARIABLE_REGEX, (match) => {
    const idx = segments.length;
    segments.push(match);
    return `${PLACEHOLDER_PREFIX}${idx}${PLACEHOLDER_SUFFIX}`;
  });
  return [result, segments];
};

const restoreVariablesAsSpans = (html: string, segments: string[]): string =>
  html.replace(
    new RegExp(`${PLACEHOLDER_PREFIX}(\\d+)${PLACEHOLDER_SUFFIX}`, "g"),
    (_m, idx) => {
      const full = segments[Number(idx)] ?? "";
      const inner = full.replace(/^\{\{|\}\}$/g, "");
      return `<span ${DATA_SPARX_VAR}="${escapeHtmlAttr(inner)}"></span>`;
    },
  );

const inlineToHtml = (node: PhrasingContent): string => {
  if (node.type === "text") {
    return escapeHtmlBody(node.value).split("\n").join("<br>");
  }
  if (node.type === "strong" && "children" in node) {
    return `<strong>${node.children.map(inlineToHtml).join("")}</strong>`;
  }
  if (node.type === "emphasis" && "children" in node) {
    return `<em>${node.children.map(inlineToHtml).join("")}</em>`;
  }
  if (node.type === "break") {
    return "<br>";
  }
  return "";
};

const paragraphToHtml = (
  node: RootContent & { type: "paragraph"; children: PhrasingContent[] },
): string => {
  const inner = node.children.map(inlineToHtml).join("");
  return `<p>${inner || "<br>"}</p>`;
};

const listItemContentToHtml = (
  children: Array<RootContent | PhrasingContent>,
): string => {
  return children
    .map((child) => {
      if (child.type === "paragraph" && "children" in child) {
        const inner = (child as { children: PhrasingContent[] }).children
          .map(inlineToHtml)
          .join("");
        return `<p>${inner || "<br>"}</p>`;
      }
      if (child.type === "list" && "children" in child) {
        return listToHtml(
          child as RootContent & {
            type: "list";
            ordered?: boolean;
            children: ListContent[];
          },
        );
      }
      return "";
    })
    .filter(Boolean)
    .join("");
};

const listToHtml = (
  node: RootContent & {
    type: "list";
    ordered?: boolean;
    children: ListContent[];
  },
): string => {
  const tag = node.ordered === true ? "ol" : "ul";
  const items = node.children
    .map((item) => {
      if (item.type !== "listItem" || !("children" in item)) return "";
      const inner = listItemContentToHtml(item.children);
      return `<li>${inner}</li>`;
    })
    .filter(Boolean)
    .join("");
  return `<${tag}>${items}</${tag}>`;
};

/** Setext / ATX headings → `<p>` (WYSIWYG schema has no Heading extension). */
const headingToHtml = (
  node: RootContent & { type: "heading"; children: PhrasingContent[] },
): string => {
  const inner = node.children.map(inlineToHtml).join("");
  return `<p>${inner || "<br>"}</p>`;
};

/** Indented/fenced code → `<p>` with escaped lines as `<br>` (no CodeBlock extension). */
const codeBlockToHtml = (
  node: RootContent & { type: "code"; value: string },
) => {
  const inner = escapeHtmlBody(node.value).split("\n").join("<br>");
  return `<p>${inner || "<br>"}</p>`;
};

/** Serialize a single block node (paragraph, thematicBreak, list, heading, code) to HTML. */
const blockToHtml = (node: RootContent): string => {
  if (node.type === "paragraph" && "children" in node) {
    return paragraphToHtml(node);
  }
  if (node.type === "thematicBreak") {
    return "<hr>";
  }
  if (node.type === "list" && "children" in node) {
    return listToHtml(
      node as RootContent & {
        type: "list";
        ordered?: boolean;
        children: ListContent[];
      },
    );
  }
  if (node.type === "heading" && "children" in node) {
    return headingToHtml(node);
  }
  if (node.type === "code" && "value" in node) {
    return codeBlockToHtml(node);
  }
  return "";
};

export const markdownToHtml = (md: string): string => {
  const { rootChildren, variableSegments } = parseSparxMarkdownAst(md);
  if (rootChildren.length === 0) return "<p></p>";
  const blocks = rootChildren.map(blockToHtml).filter(Boolean).join("");
  const html = blocks || "<p></p>";
  return restoreVariablesAsSpans(html, variableSegments);
};

const SPAN_VAR_REGEX = new RegExp(
  `<span\\s+${DATA_SPARX_VAR}="((?:[^"&]|&[^;]*;)*)"[^>]*>\\s*</span>`,
  "gi",
);

const decodeHtmlEntities = (s: string): string =>
  s
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

const htmlFragmentToMarkdownLine = (fragment: string): string => {
  let t = fragment
    .replace(/<p>/gi, "\n\n")
    .replace(/<\/p>/gi, "")
    .replace(/<br\s*\/?>/gi, MARKDOWN_HARD_BREAK_LINE_ENDING)
    .replace(/<strong>(.+?)<\/strong>/gi, "**$1**")
    .replace(/<em>(.+?)<\/em>/gi, "*$1*")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
  t = trimMarkdownOuterNewlines(collapseNewlines(t));
  return t.replace(/\n+/g, " ");
};

const listBlockToMarkdown = (listHtml: string, ordered: boolean): string => {
  const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
  const items: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = liRegex.exec(listHtml)) !== null) {
    items.push(htmlFragmentToMarkdownLine(m[1] ?? ""));
  }
  if (ordered) {
    return items.map((line, i) => `${i + 1}. ${line}`).join("\n");
  }
  return items.map((line) => `- ${line}`).join("\n");
};

export const htmlToMarkdown = (html: string): string => {
  let s = html.replace(SPAN_VAR_REGEX, (_match, attrValue: string) => {
    const decoded = decodeHtmlEntities(attrValue);
    return `{{${decoded}}}`;
  });
  s = s.replace(/<hr\s*\/?>/gi, "\n\n---\n\n");

  // Convert list blocks before stripping <p>/<strong>/<em> so list item content is preserved.
  s = s.replace(
    /<ul[^>]*>([\s\S]*?)<\/ul>/gi,
    (_match: string, inner: string) => {
      return "\n\n" + listBlockToMarkdown(inner, false) + "\n\n";
    },
  );
  s = s.replace(
    /<ol[^>]*>([\s\S]*?)<\/ol>/gi,
    (_match: string, inner: string) => {
      return "\n\n" + listBlockToMarkdown(inner, true) + "\n\n";
    },
  );

  s = s
    .replace(/<p>/gi, "\n\n")
    .replace(/<\/p>/gi, "")
    .replace(/<br\s*\/?>/gi, MARKDOWN_HARD_BREAK_LINE_ENDING)
    .replace(/<strong>(.+?)<\/strong>/gi, "**$1**")
    .replace(/<em>(.+?)<\/em>/gi, "*$1*")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
  s = restoreMarkdownIndentNbsp(trimMarkdownOuterNewlines(collapseNewlines(s)));
  return s;
};
